import { Dispatch } from "redux";
import axios from "../../utils/axios.tsx";
import { loginAsAnonymousUser } from "../../store/auth.tsx";
import {
  conversationActions,
  fetchConversations,
} from "../../store/conversation.tsx";
import { BASE_URL } from "../../const.ts";
import { RootState } from "../../store/types.tsx";

// Local type for streaming response data
interface StreamableResponse {
  content: string;
  /** A progress update's line for voice mode to say, if the step has one. */
  spoken_content?: string;
  progress_update: boolean;
  done: boolean;
  response_type: string;
}

interface ExchangeParams {
  conversation_id?: string;
  exchange_id?: string;
  prompt: string;
  model?: string;
}

interface AskQuestionParams {
  dispatch: Dispatch;
  getState: () => RootState;
  isAuthenticated: boolean;
  stopAnimation: () => void;
  exchangeURL: string;
  streamSlug: string;
  exchangeParams: ExchangeParams;
}

// Module-level registry of stream-stop handles, keyed by conversation id. This lives
// outside any component so it survives the unmount of whichever component started the
// stream (e.g. SearchField unmounting when the circle view takes over) — stopping a
// conversation's stream must work regardless of what is currently mounted.
const stopHandlersByConversationId = new Map<string, () => void>();

export function registerStopHandler(
  conversationId: string,
  handler: () => void,
): void {
  stopHandlersByConversationId.set(conversationId, handler);
}

function clearStopHandler(conversationId: string): void {
  stopHandlersByConversationId.delete(conversationId);
}

export function hasStopHandler(conversationId: string): boolean {
  return stopHandlersByConversationId.has(conversationId);
}

export function stopStream(conversationId: string): void {
  const handler = stopHandlersByConversationId.get(conversationId);
  if (handler) {
    handler();
  }
}

export async function streamAsk({
  dispatch,
  getState,
  isAuthenticated,
  stopAnimation,
  exchangeURL,
  streamSlug,
  exchangeParams,
}: AskQuestionParams): Promise<void> {
  const streamUrl = `${BASE_URL}${streamSlug}`;
  const initialConversationId = exchangeParams.conversation_id || "new";
  let activeConversationId = initialConversationId;

  // Only streamAsk knows what the user was looking at when the answer landed, since it
  // is the only thing still running once the component that started the question may
  // have unmounted or navigated away.
  const settleActivity = (conversationId: string) => {
    const state = getState();
    const isBeingViewed =
      state.conversationState.selectedConversation === conversationId &&
      state.conversationState.selectedView === "conversation";
    dispatch(
      isBeingViewed
        ? conversationActions.clearConversationActivity(conversationId)
        : conversationActions.setConversationReady(conversationId),
    );
  };

  dispatch(conversationActions.setConversationWorking(initialConversationId));

  // The whole exchange kickoff (POST + validation) is wrapped so any failure here —
  // a rejected request or an unexpected response shape — still settles activity
  // instead of leaving the conversation permanently "working" (which would also keep
  // consuming a slot against the concurrency cap).
  let exchangeResponse: any;
  try {
    exchangeResponse = await axios.post(
      exchangeURL,
      JSON.stringify(exchangeParams),
      { withCredentials: true },
    );

    if (!isAuthenticated && exchangeResponse.status == 429) {
      const conversationId = exchangeParams.conversation_id || "new";
      dispatch(loginAsAnonymousUser());
      dispatch(
        conversationActions.appendCustomActionResponse({
          conversation_id: conversationId,
          prompt: exchangeParams.prompt,
          text: exchangeResponse.statusText,
          type: "TextResponse",
          savePrompt: true,
          progressUpdate: false,
        }),
      );
      dispatch(conversationActions.setSelectedConversation(conversationId));
      stopAnimation();
      settleActivity(initialConversationId);
      return;
    }
    if (!exchangeResponse || !exchangeResponse.data) {
      throw new Error("Failed to initiate streaming exchange.");
    }
    if (
      exchangeResponse.data.prompt === "NEW CONVERSATION" ||
      exchangeResponse.data.prompt.toUpperCase() === "!NC"
    ) {
      dispatch(conversationActions.resetExchanges());
      dispatch(conversationActions.loadAllExchanges([exchangeResponse.data]));
      dispatch(fetchConversations() as any);
      dispatch(
        conversationActions.setSelectedConversation(
          exchangeResponse.data.conversation_id,
        ),
      );
      stopAnimation();
      settleActivity(initialConversationId);
      return;
    }
  } catch (error) {
    console.error("Error initiating exchange:", error);
    stopAnimation();
    settleActivity(activeConversationId);
    return;
  }

  activeConversationId = exchangeResponse.data.conversation_id;
  if (activeConversationId !== initialConversationId) {
    dispatch(
      conversationActions.moveConversationActivity({
        fromId: initialConversationId,
        toId: activeConversationId,
      }),
    );
  }

  if (!isAuthenticated) {
    dispatch(
      conversationActions.setSelectedConversation(
        exchangeResponse.data.conversation_id,
      ),
    );
  }

  dispatch(
    conversationActions.appendExchange({
      conversation_id: exchangeResponse.data.conversation_id,
      id: exchangeResponse.data.id,
      prompt: exchangeResponse.data.prompt,
      response: null,
    }),
  );

  // Give the conversation a drawer row immediately, while it is still answering,
  // instead of waiting for the stream to end. The refresh at the end of the stream is
  // still needed separately: it is what picks up the generated title.
  // Anonymous users have no drawer, and their GET /v1/conversation/ 401s, which the
  // axios interceptor turns into logout() — dropping them back to the circle page.
  if (isAuthenticated) {
    dispatch(fetchConversations() as any);
  }

  exchangeParams["conversation_id"] = exchangeResponse.data.conversation_id;

  // Set once the user explicitly stops the stream, so the finally block below skips
  // the ready/clear settle rule: a stopped answer is always cleared, never badged.
  let stoppedByUser = false;

  try {
    if (!isAuthenticated) {
      dispatch(loginAsAnonymousUser());
    }

    // Step 2: Start the streaming using SSE hook approach
    const signedValue = exchangeResponse.data.signed_value;
    let streamUrlWithParams = `${streamUrl}?user_signature=${encodeURIComponent(signedValue)}`;
    let conversationId = exchangeParams["conversation_id"];
    if (conversationId) {
      streamUrlWithParams += `&conversation_id=${exchangeParams["conversation_id"]}`;
    }

    let accumulatedContent = "";
    let lastUpdateTime = 0;
    const UPDATE_THROTTLE_MS = 100; // Throttle updates to every 100ms

    await new Promise<void>((resolve, reject) => {
      const eventSource = new EventSource(streamUrlWithParams);

      registerStopHandler(activeConversationId, () => {
        console.log("User stopped streaming");
        stoppedByUser = true;
        eventSource.close();
        clearStopHandler(activeConversationId);
        stopAnimation();
        dispatch(
          conversationActions.updateExchangeById({
            conversationId: exchangeResponse.data.conversation_id,
            id: exchangeResponse.data.id,
            updateFields: { progress_update: false },
          }),
        );
        // The user knows they stopped it; never badge a stopped answer as "ready".
        dispatch(
          conversationActions.clearConversationActivity(activeConversationId),
        );
        if (isAuthenticated) {
          dispatch(fetchConversations() as any);
        }
        // Settle the wrapping promise so this call's finally block still runs (it just
        // skips the ready/clear settle rule above, via stoppedByUser).
        resolve();
      });

      eventSource.onopen = () => {
        console.log("Streaming connection opened.");
      };

      eventSource.onmessage = (event) => {
        let askResponse;
        try {
          askResponse = JSON.parse(event.data);
          console.log(askResponse);
        } catch (error) {
          console.error("Error parsing SSE message:", error, event.data);
          return;
        }

        if (
          askResponse.type === "completed" ||
          askResponse.type === "stream_ended"
        ) {
          console.log("End of stream received.");
          // Final update with complete content
          if (accumulatedContent) {
            dispatch(
              conversationActions.appendExchange({
                conversation_id: exchangeResponse.data.conversation_id,
                response: accumulatedContent,
                type: "TextResponse",
                progress_update: false,
              }),
            );
          }
          eventSource.close();
          resolve();
        } else if (askResponse.type === "status") {
          const responseData = askResponse.data as StreamableResponse;

          try {
            if (responseData.progress_update) {
              if (responseData.done) {
                // Final completion - no more content accumulation needed
                return;
              } else {
                // Skip progress updates with empty content to prevent component unmounting
                if (
                  !responseData.content ||
                  responseData.content.trim() === ""
                ) {
                  console.log(
                    "🚫 Skipping empty progress update to prevent unmount",
                  );
                  return;
                }
                // Progress update - send as-is (client will handle dot animation)
                dispatch(
                  conversationActions.appendExchange({
                    conversation_id: exchangeResponse.data.conversation_id,
                    response: responseData.content,
                    type: responseData.response_type,
                    progress_update: true,
                    spoken_progress: responseData.spoken_content ?? "",
                  }),
                );
              }
            } else if (
              responseData.response_type === "Graph" ||
              responseData.response_type === "Table"
            ) {
              // Handle graphs/tables as complete objects - no accumulation
              const content = JSON.parse(responseData.content);
              dispatch(
                conversationActions.appendExchange({
                  conversation_id: exchangeResponse.data.conversation_id,
                  response: content,
                  type: responseData.response_type,
                  progress_update: false,
                }),
              );
            } else {
              // Text content - accumulate incremental chunks
              accumulatedContent += responseData.content;

              // Throttle updates to reduce flickering
              const now = Date.now();
              if (now - lastUpdateTime >= UPDATE_THROTTLE_MS) {
                dispatch(
                  conversationActions.appendExchange({
                    conversation_id: exchangeResponse.data.conversation_id,
                    response: accumulatedContent,
                    type: responseData.response_type,
                    progress_update: false,
                  }),
                );
                lastUpdateTime = now;
              }
            }
          } catch (error) {
            console.error("Error processing stream data:", error);
          }
        }
      };

      eventSource.onerror = (error) => {
        console.error("Streaming error:", error);
        eventSource.close();
        reject(error);
      };
    });
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    stopAnimation();
    clearStopHandler(activeConversationId);
    if (isAuthenticated) {
      dispatch(fetchConversations() as any);
    }
    if (!stoppedByUser) {
      settleActivity(activeConversationId);
    }
  }
}
